import * as Network from 'expo-network';
import { AppState, Platform } from 'react-native';

import { logError } from '@helpers/Loggers';
import { focusManager, onlineManager } from '@tanstack/react-query';

let setupCount = 0;
type ListenerSubscription = (() => void) | { remove?: unknown };

export function setupAppQueryEnvironment() {
  setupCount += 1;
  if (setupCount > 1) return cleanupAppQueryEnvironment;

  setupAppQueryFocusManager();
  setupAppQueryOnlineManager();

  return cleanupAppQueryEnvironment;
}

function cleanupAppQueryEnvironment() {
  setupCount = Math.max(0, setupCount - 1);
  if (setupCount > 0) return;

  focusManager.setEventListener(() => undefined);
  onlineManager.setEventListener(() => undefined);
}

function setupAppQueryFocusManager() {
  if (Platform.OS === 'web') return;

  focusManager.setEventListener((setFocused) => {
    setFocused(AppState.currentState === 'active');
    const subscription = callPlatformListenerSafely(() =>
      AppState.addEventListener('change', (status) => {
        setFocused(status === 'active');
      })
    );

    return () => removeSubscription(subscription);
  });
}

function setupAppQueryOnlineManager() {
  onlineManager.setEventListener((setOnline) => {
    callPlatformAsyncSafely(() => Network.getNetworkStateAsync())
      .then((state) => setOnline(isNetworkOnline(state)))
      .catch((error) => logError(error));

    const subscription = callPlatformListenerSafely(() =>
      Network.addNetworkStateListener((state) => {
        setOnline(isNetworkOnline(state));
      })
    );

    return () => removeSubscription(subscription);
  });
}

function isNetworkOnline(state: Network.NetworkState | undefined) {
  if (!state) return true;
  if (state.type === Network.NetworkStateType.NONE) return false;
  if (state.isConnected === false) return false;
  if (state.isInternetReachable === false) return false;

  return true;
}

async function callPlatformAsyncSafely<T>(callback: () => Promise<T> | T) {
  return await callback();
}

function callPlatformListenerSafely<TSubscription extends ListenerSubscription>(
  callback: () => TSubscription
): TSubscription | undefined {
  try {
    return callback();
  } catch (error) {
    logError(error);
    return undefined;
  }
}

function removeSubscription(subscription: ListenerSubscription | undefined) {
  if (!subscription) return;
  if (typeof subscription === 'function') {
    subscription();
    return;
  }

  if (typeof subscription.remove === 'function') {
    subscription.remove();
  }
}
